/**
 * GitHub API client for Factory Git integration.
 *
 * Provides typed API calls with ETag caching support.
 */

import { api } from "./api";

// ============================================================================
// Types
// ============================================================================

export interface GitHubInstallation {
  id: number;
  installationId: number;
  accountLogin: string;
  accountType: string;
  accountAvatarUrl?: string;
  reposCount?: number;
  connectedAt?: string;
}

export interface GitHubRepo {
  id: number;
  fullName: string;
  name: string;
  private: boolean;
  defaultBranch: string;
  updatedAt: string;
  description?: string;
}

export interface GitHubBranch {
  name: string;
  sha: string;
  protected: boolean;
}

export interface GitHubCommitAuthor {
  name: string;
  email: string;
  date: string;
  avatarUrl?: string;
  login?: string;
}

export interface GitHubCommit {
  sha: string;
  message: string;
  author: GitHubCommitAuthor;
  date: string;
  additions: number;
  deletions: number;
  url: string;
}

export interface GitHubPullRequest {
  number: number;
  title: string;
  state: string;
  draft: boolean;
  headRef: string;
  baseRef: string;
  headSha: string;
  userLogin: string;
  userAvatarUrl: string;
  createdAt: string;
  updatedAt: string;
  additions: number;
  deletions: number;
  changedFiles: number;
  url: string;
  body?: string;
}

export interface GitHubPAT {
  id: number;
  workspaceId: number;
  tokenHint?: string;
  githubLogin?: string;
  scopes: string[];
  validatedAt?: string;
  createdAt: string;
  git_name?: string;
  git_email?: string;
}

export interface GitHubPATValidation {
  valid: boolean;
  githubLogin?: string;
  scopes: string[];
  error?: string;
  git_name?: string;
  git_email?: string;
}

export interface GitHubIssueInfo {
  owner: string;
  repo: string;
  number: number;
}

// API response types (snake_case from backend)
interface ApiInstallation {
  id: number;
  installation_id: number;
  account_login: string;
  account_type: string;
  account_avatar_url?: string;
  repos_count?: number;
  connected_at?: string;
}

interface ApiRepo {
  id: number;
  full_name: string;
  name: string;
  private: boolean;
  default_branch: string;
  updated_at: string;
  description?: string;
}

interface ApiBranch {
  name: string;
  sha: string;
  protected: boolean;
}

interface ApiCommitAuthor {
  name: string;
  email: string;
  date: string;
  avatar_url?: string;
  login?: string;
}

interface ApiCommit {
  sha: string;
  message: string;
  author: ApiCommitAuthor;
  date: string;
  additions: number;
  deletions: number;
  url: string;
}

interface ApiPullRequest {
  number: number;
  title: string;
  state: string;
  draft: boolean;
  head_ref: string;
  base_ref: string;
  head_sha: string;
  user_login: string;
  user_avatar_url: string;
  created_at: string;
  updated_at: string;
  additions: number;
  deletions: number;
  changed_files: number;
  url: string;
  body?: string;
}

interface ApiPAT {
  id: number;
  workspace_id: number;
  token_hint?: string;
  github_login?: string;
  scopes: string[];
  validated_at?: string;
  created_at: string;
  git_name?: string;
  git_email?: string;
}

interface ApiPATValidation {
  valid: boolean;
  github_login?: string;
  scopes: string[];
  error?: string;
  git_name?: string;
  git_email?: string;
}

// ============================================================================
// Mappers (snake_case -> camelCase)
// ============================================================================

function mapInstallation(a: ApiInstallation): GitHubInstallation {
  return {
    id: a.id,
    installationId: a.installation_id,
    accountLogin: a.account_login,
    accountType: a.account_type,
    accountAvatarUrl: a.account_avatar_url,
    reposCount: a.repos_count,
    connectedAt: a.connected_at,
  };
}

function mapRepo(r: ApiRepo): GitHubRepo {
  return {
    id: r.id,
    fullName: r.full_name,
    name: r.name,
    private: r.private,
    defaultBranch: r.default_branch,
    updatedAt: r.updated_at,
    description: r.description,
  };
}

function mapBranch(b: ApiBranch): GitHubBranch {
  return {
    name: b.name,
    sha: b.sha,
    protected: b.protected,
  };
}

function mapCommit(c: ApiCommit): GitHubCommit {
  return {
    sha: c.sha,
    message: c.message,
    author: {
      name: c.author.name,
      email: c.author.email,
      date: c.author.date,
      avatarUrl: c.author.avatar_url,
      login: c.author.login,
    },
    date: c.date,
    additions: c.additions,
    deletions: c.deletions,
    url: c.url,
  };
}

function mapPullRequest(pr: ApiPullRequest): GitHubPullRequest {
  return {
    number: pr.number,
    title: pr.title,
    state: pr.state,
    draft: pr.draft,
    headRef: pr.head_ref,
    baseRef: pr.base_ref,
    headSha: pr.head_sha,
    userLogin: pr.user_login,
    userAvatarUrl: pr.user_avatar_url,
    createdAt: pr.created_at,
    updatedAt: pr.updated_at,
    additions: pr.additions,
    deletions: pr.deletions,
    changedFiles: pr.changed_files,
    url: pr.url,
    body: pr.body,
  };
}

function mapPAT(p: ApiPAT): GitHubPAT {
  return {
    id: p.id,
    workspaceId: p.workspace_id,
    tokenHint: p.token_hint,
    githubLogin: p.github_login,
    scopes: p.scopes || [],
    validatedAt: p.validated_at,
    createdAt: p.created_at,
    git_name: p.git_name,
    git_email: p.git_email,
  };
}

function mapPATValidation(v: ApiPATValidation): GitHubPATValidation {
  return {
    valid: v.valid,
    githubLogin: v.github_login,
    scopes: v.scopes || [],
    error: v.error,
    git_name: v.git_name,
    git_email: v.git_email,
  };
}

// ============================================================================
// Types for auth
// ============================================================================

export type AuthType = "installation" | "pat";

export interface AuthParams {
  workspaceId: number;
  installationId?: number | null;
  authType?: AuthType;
}

function buildAuthQuery(params: AuthParams): string {
  const parts = [`workspace_id=${params.workspaceId}`];
  if (params.installationId) {
    parts.push(`installation_id=${params.installationId}`);
  }
  if (params.authType) {
    parts.push(`auth_type=${params.authType}`);
  }
  return parts.join("&");
}

// ============================================================================
// GitHub Issue URL Helpers
// ============================================================================

/**
 * Parse GitHub issue URL to extract owner, repo, and issue number
 */
export function parseGitHubIssueUrl(url: string): GitHubIssueInfo | null {
  if (!url) {
    return null;
  }

  try {
    const regex = /^https?:\/\/github\.com\/([^\/]+)\/([^\/]+)\/issues\/(\d+)/;
    const match = url.match(regex);

    if (!match) {
      return null;
    }

    const [, owner, repo, numberStr] = match;
    const number = parseInt(numberStr, 10);

    if (!owner || !repo || isNaN(number)) {
      return null;
    }

    return { owner, repo, number };
  } catch {
    return null;
  }
}

// ============================================================================
// API Functions
// ============================================================================

export const githubApi = {
  // ---- Installations ----

  async listAvailableInstallations(): Promise<GitHubInstallation[]> {
    const data = await api.get<ApiInstallation[]>("/api/github/installations");
    return (data || []).map(mapInstallation);
  },

  async listWorkspaceInstallations(workspaceId: number): Promise<GitHubInstallation[]> {
    const data = await api.get<ApiInstallation[]>(
      `/api/github/workspaces/${workspaceId}/installations`
    );
    return (data || []).map(mapInstallation);
  },

  async connectInstallation(workspaceId: number, installationId: number): Promise<GitHubInstallation> {
    const data = await api.post<ApiInstallation>(
      `/api/github/workspaces/${workspaceId}/installations`,
      { installation_id: installationId }
    );
    return mapInstallation(data);
  },

  async disconnectInstallation(workspaceId: number, installationDbId: number): Promise<void> {
    await api.delete(`/api/github/workspaces/${workspaceId}/installations/${installationDbId}`);
  },

  // ---- Repositories ----

  async listRepos(workspaceId: number, installationId: number): Promise<GitHubRepo[]> {
    const data = await api.get<ApiRepo[]>(
      `/api/github/workspaces/${workspaceId}/installations/${installationId}/repos`
    );
    return (data || []).map(mapRepo);
  },

  async listPATRepos(workspaceId: number, perPage: number = 100, page: number = 1): Promise<GitHubRepo[]> {
    const data = await api.get<ApiRepo[]>(
      `/api/github/pat/workspaces/${workspaceId}/repos?per_page=${perPage}&page=${page}`
    );
    return (data || []).map(mapRepo);
  },

  // ---- Branches ----

  async listBranches(
    owner: string,
    repo: string,
    auth: AuthParams,
    perPage: number = 30,
    page: number = 1
  ): Promise<GitHubBranch[]> {
    const query = buildAuthQuery(auth);
    const data = await api.get<ApiBranch[]>(
      `/api/github/repos/${owner}/${repo}/branches?${query}&per_page=${perPage}&page=${page}`
    );
    return (data || []).map(mapBranch);
  },

  async createBranch(
    owner: string,
    repo: string,
    auth: AuthParams,
    name: string,
    fromBranch: string
  ): Promise<GitHubBranch> {
    const query = buildAuthQuery(auth);
    const data = await api.post<ApiBranch>(
      `/api/github/repos/${owner}/${repo}/branches?${query}`,
      { name, from_branch: fromBranch }
    );
    return mapBranch(data);
  },

  // ---- Commits ----

  async listCommits(
    owner: string,
    repo: string,
    auth: AuthParams,
    branch: string = "main",
    limit: number = 20,
    page: number = 1
  ): Promise<GitHubCommit[]> {
    const query = buildAuthQuery(auth);
    const data = await api.get<ApiCommit[]>(
      `/api/github/repos/${owner}/${repo}/commits?${query}&branch=${encodeURIComponent(branch)}&limit=${limit}&page=${page}`
    );
    return (data || []).map(mapCommit);
  },

  // ---- Pull Requests ----

  async listPullRequests(
    owner: string,
    repo: string,
    auth: AuthParams,
    state: "open" | "closed" | "all" = "open",
    perPage: number = 20,
    page: number = 1
  ): Promise<GitHubPullRequest[]> {
    const query = buildAuthQuery(auth);
    const data = await api.get<ApiPullRequest[]>(
      `/api/github/repos/${owner}/${repo}/pulls?${query}&state=${state}&per_page=${perPage}&page=${page}`
    );
    return (data || []).map(mapPullRequest);
  },

  async createPullRequest(
    owner: string,
    repo: string,
    auth: AuthParams,
    title: string,
    body: string,
    head: string,
    base: string,
    draft: boolean = false
  ): Promise<GitHubPullRequest> {
    const query = buildAuthQuery(auth);
    const data = await api.post<ApiPullRequest>(
      `/api/github/repos/${owner}/${repo}/pulls?${query}`,
      { title, body, head, base, draft }
    );
    return mapPullRequest(data);
  },

  // ---- Issues ----

  async updateIssueLabels(
    owner: string,
    repo: string,
    number: number,
    labels: string[],
    auth: AuthParams,
    state?: 'open' | 'closed'
  ): Promise<unknown> {
    const body: { labels: string[]; state?: string } = { labels };
    if (state) {
      body.state = state;
    }

    const query = buildAuthQuery(auth);
    return api.patch(`/api/github/repos/${owner}/${repo}/issues/${number}?${query}`, body);
  },

  async createIssue(
    owner: string,
    repo: string,
    data: { title: string; body?: string; labels?: string[]; assignees?: string[] },
    auth: AuthParams,
  ): Promise<{ number: number; url?: string; html_url?: string; title: string; state: string; labels: string[] }> {
    const query = buildAuthQuery(auth);
    return api.post(`/api/github/repos/${owner}/${repo}/issues?${query}`, data);
  },

  // ---- Utils ----

  async getDefaultBranch(
    owner: string,
    repo: string,
    auth: AuthParams
  ): Promise<string> {
    const query = buildAuthQuery(auth);
    const data = await api.get<{ default_branch: string }>(
      `/api/github/repos/${owner}/${repo}/default-branch?${query}`
    );
    return data.default_branch;
  },

  // ---- PAT ----

  async getWorkspacePAT(workspaceId: number): Promise<GitHubPAT> {
    const data = await api.get<ApiPAT>(
      `/api/github/pat/workspaces/${workspaceId}/pat`
    );
    return mapPAT(data);
  },

  async createWorkspacePAT(workspaceId: number, token: string): Promise<GitHubPAT> {
    const data = await api.post<ApiPAT>(
      `/api/github/pat/workspaces/${workspaceId}/pat`,
      { token }
    );
    return mapPAT(data);
  },

  async deleteWorkspacePAT(workspaceId: number): Promise<void> {
    await api.delete(`/api/github/pat/workspaces/${workspaceId}/pat`);
  },

  async validatePAT(token: string): Promise<GitHubPATValidation> {
    const data = await api.post<ApiPATValidation>(
      `/api/github/pat/validate`,
      { token }
    );
    return mapPATValidation(data);
  },
};
